import { ActionButton, Cell, Column, Content, DialogContainer, Flex, Link, ProgressCircle, Row, TableBody, TableHeader, TableView, ToastQueue, View } from '@adobe/react-spectrum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleFill, InfoCircle } from 'react-bootstrap-icons';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { FPAData, FPADataTypes } from '../../../../infra/protected/FPA/FPAData';
import { GetSchedulerSettingsRequest, SettingsItemType } from '../../../../services/soap/scheduler/requests/GetSchedulerSettingsRequest';
import { ListIssueRequest } from '../../../../services/soap/scheduler/requests/ListIssueRequest';
import { ListUserRequest } from '../../../../services/soap/access/requests/ListUserRequest';
import { TaskTemplateListRequest } from '../../../../services/soap/scheduler/requests/TaskTemplateListRequest';
import { GetContextPathRequest } from '../../../../services/soap/project/requests/GetContextPathRequest';
import { ILayoutComponentProps, LayoutComponent } from '../LayoutComponent';
import { statusColor } from '../../../../utils/helperUtil';
import IssueTemplatePickerDialog from '../../../../pages/protected/issue/components/IssueTemplatePickerDialog';
import CreateIssueDialog from '../../../../pages/protected/issue/components/CreateIssueDialog';
import { invokeIssueApp } from '../../../../utils/NavigationUtils';
import styles from './ContextIssueGridComponent.module.css';

type IssueTemplate = {
  id: string;
  acl?: string;
  name: string;
  taskTitle?: string;
  typeOfWork?: string;
  status?: string;
  priority?: string;
  reminder?: string;
  solver?: string;
  solverName?: string;
  repeat?: {
    repeatMode?: string;
    validFrom?: string;
    validTo?: string;
    repeatEvery?: string;
    repeatDay?: string;
    repeatMonth?: string;
    repeatWeekDay?: string;
    repeatWeekCount?: string;
    repeatWeekDayList?: string;
    timeFrom?: string;
    timeTo?: string;
    useOffset?: string;
    offset?: string;
    limit?: string;
  };
};

interface IContextIssueGridProps extends ILayoutComponentProps {
  selectedItem?: FPAData;
}

function ContextIssueGrid({ selectedItem }: IContextIssueGridProps) {
  const { t } = useTranslation(['issue', 'layout_components']);
  const { schedulerService, accessService, projectService, store } = useDependency();
  const [isLoading, setIsLoading] = useState(false);
  const [rowsData, setRowsData] = useState<any[]>([]);
  const [issueTypes, setIssueTypes] = useState<Array<{ id: string; name: string; formId?: string; formName?: string; color?: string }>>([]);
  const [issueStatusList, setIssueStatusList] = useState<any[]>([]);
  const [userData, setUserData] = useState<Array<{ id: string; name: string }>>([]);
  const [issueTemplates, setIssueTemplates] = useState<IssueTemplate[]>([]);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<IssueTemplate | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const dataRef = useRef<{ folderID?: string }>({});

  const issueTypeMap = useMemo(
    () =>
      issueTypes.reduce<Record<string, string>>((acc, item) => {
        acc[String(item.id)] = item.name;
        return acc;
      }, {}),
    [issueTypes]
  );

  const issueTypeSettingsMap = useMemo(
    () =>
      issueTypes.reduce<Record<string, { id: string; name: string; formId?: string; formName?: string; color?: string }>>((acc, item) => {
        acc[String(item.id)] = item;
        return acc;
      }, {}),
    [issueTypes]
  );

  const issueStatusMap = useMemo(
    () =>
      issueStatusList.reduce<Record<string, any>>((acc, item) => {
        acc[String(item.ident)] = item;
        return acc;
      }, {}),
    [issueStatusList]
  );

  const normalizeResponseItems = useCallback((payload: any) => {
    if (!payload) return [];
    if (Array.isArray(payload)) return payload;
    if (typeof payload === 'object') {
      const keys = Object.keys(payload);
      const numericKeys = keys.filter((key) => /^\d+$/.test(key));
      if (numericKeys.length > 0) {
        return numericKeys.sort((a, b) => Number(a) - Number(b)).map((key) => payload[key]);
      }
      return [payload];
    }
    return [];
  }, []);

  const getIssueStatusList = useCallback(async () => {
    const statusArray: any[] = [];
    const res: any = await schedulerService.getSchedulerSettings(
      new GetSchedulerSettingsRequest(store.Server, store.SessionId, '1', SettingsItemType.ISSUE_ITEM_TYPE, 50, 0)
    );
    if (res?.result === 'OK' && res.ITEMS?.length > 0) {
      res.ITEMS.forEach((status: any) => {
        const { ident, title, color } = status.ITEM;
        statusArray.push({ ident: String(ident), title, color: statusColor(color) });
      });
      setIssueStatusList(statusArray);
      return statusArray;
    }
    setIssueStatusList([]);
    return [];
  }, [schedulerService, store.Server, store.SessionId]);

  const getUserData = useCallback(async () => {
    const userList: any = await accessService.listUser(new ListUserRequest(store.Server, store.SessionId, true, 500));
    if (userList?.result === 'OK') {
      const formatted = (userList.USERS || []).map(({ USER }: any) => ({
        id: String(USER.id),
        name:
          `${USER.name1 || ''} ${USER.name3 || ''}`.trim() ||
          String(USER.name || USER.login || USER.username || USER.email || USER.id || '').trim(),
      }));
      setUserData(formatted);
      return formatted;
    }
    setUserData([]);
    return [];
  }, [accessService, store.Server, store.SessionId]);

  const getIssueTypes = useCallback(async () => {
    const response: any = await schedulerService.getSchedulerSettings(
      new GetSchedulerSettingsRequest(store.Server, store.SessionId, '4', SettingsItemType.ISSUE_ITEM_TYPE, 50, 0)
    );
    if (response?.result === 'OK') {
      const types = normalizeResponseItems(response.ITEMS || response.ITEM)
        .map((entry: any) => {
          const item = entry?.ITEM || entry;
          return {
            id: String(item?.ident || item?.id || ''),
            name: String(item?.title || item?.name || item?.entryTypeName || 'Unknown'),
            formId: item?.preTaskChecklistForm ? String(item.preTaskChecklistForm) : undefined,
            formName: item?.preTaskChecklistFormName ? String(item.preTaskChecklistFormName) : undefined,
            color: item?.color ? statusColor(item.color) : undefined,
          };
        })
        .filter((item: any) => item.id);
      setIssueTypes(types);
      return types;
    }
    setIssueTypes([]);
    return [];
  }, [normalizeResponseItems, schedulerService, store.Server, store.SessionId]);

  const getIssueTemplates = useCallback(async () => {
    const response: any = await schedulerService.taskTemplateList(
      new TaskTemplateListRequest(store.Server, store.SessionId, 100, 'i', 0)
    );
    const responseArray = Array.isArray(response) ? response : [response];
    const templateItems = responseArray.filter((entry: any) => entry?.ITEM);
    const templates = templateItems
      .map((entry: any) => {
        const item = entry?.ITEM || entry;
        const solverId = String(item?.solver || '');
        const solverName =
          userData.find((user) => String(user.id) === solverId)?.name ||
          solverId ||
          t('issue_template_picker.unassigned', { ns: 'issue' });
        return {
          id: String(item?.id || item?.ident || ''),
          acl: String(item?.acl || ''),
          name: String(item?.name || ''),
          taskTitle: String(item?.taskTitle || item?.name || ''),
          typeOfWork: String(item?.typeOfWork || ''),
          status: String(item?.status || ''),
          priority: String(item?.priority || '0'),
          reminder: String(item?.reminder || '0'),
          solver: solverId,
          solverName,
          repeat: item?.REPEAT
            ? {
                repeatMode: String(item.REPEAT.repeatMode || ''),
                validFrom: String(item.REPEAT.validFrom || ''),
                validTo: String(item.REPEAT.validTo || ''),
                repeatEvery: String(item.REPEAT.repeatEvery || ''),
                repeatDay: String(item.REPEAT.repeatDay || ''),
                repeatMonth: String(item.REPEAT.repeatMonth || ''),
                repeatWeekDay: String(item.REPEAT.repeatWeekDay || ''),
                repeatWeekCount: String(item.REPEAT.repeatWeekCount || ''),
                repeatWeekDayList: String(item.REPEAT.repeatWeekDayList || ''),
                timeFrom: String(item.REPEAT.timeFrom || ''),
                timeTo: String(item.REPEAT.timeTo || ''),
                useOffset: String(item.REPEAT.useOffset || ''),
                offset: String(item.REPEAT.offset || ''),
                limit: String(item.REPEAT.limit || ''),
              }
            : undefined,
        };
      })
      .filter((item: IssueTemplate) => item.id);
    setIssueTemplates(templates);
    return templates;
  }, [schedulerService, store.Server, store.SessionId, t, userData]);

  useEffect(() => {
    let isMounted = true;

    const loadContextPath = async () => {
      if (!selectedItem?.id) {
        dataRef.current = {};
        return;
      }

      try {
        const response: any = await projectService.getContextPath(
          new GetContextPathRequest(store.Server, store.SessionId, Number(selectedItem.id))
        );
        const folderID = selectedItem
          ? response?.LIST?.[selectedItem.type === FPADataTypes.ACTIVITY ? 1 : 0]?.CONTEXT[
              selectedItem.type === FPADataTypes.FOLDER ? 'id' : 'parentId'
            ]
          : undefined;

        if (isMounted) {
          dataRef.current = { folderID: folderID ? String(folderID) : undefined };
        }
      } catch {
        if (isMounted) {
          dataRef.current = {};
        }
      }
    };

    void loadContextPath();

    return () => {
      isMounted = false;
    };
  }, [projectService, selectedItem, store.Server, store.SessionId]);

  const getContextFilter = useCallback(() => {
    if (!selectedItem) return {};
    switch (selectedItem.type) {
      case FPADataTypes.ACTIVITY:
        return {
          folderId: String(dataRef.current.folderID || ''),
          projectId: String(selectedItem.parent_id || ''),
          activityId: String(selectedItem.id || ''),
        };
      case FPADataTypes.PROJECT:
        return {
          folderId: String(selectedItem.parent_id || ''),
          projectId: String(selectedItem.id || ''),
        };
      case FPADataTypes.FOLDER:
        return {
          folderId: String(selectedItem.id || ''),
        };
      default:
        return {};
    }
  }, [selectedItem]);

  const getIssueCreateContext = useCallback(() => {
    const issueCreateContext = {
      activityId: '',
      projectId: '',
      folderId: '',
    };

    switch (selectedItem?.type) {
      case FPADataTypes.ACTIVITY:
        issueCreateContext.folderId = String(dataRef.current.folderID || '');
        issueCreateContext.projectId = String(selectedItem.parent_id || '');
        issueCreateContext.activityId = String(selectedItem.id || '');
        break;
      case FPADataTypes.PROJECT:
        issueCreateContext.folderId = String(selectedItem.parent_id || '');
        issueCreateContext.projectId = String(selectedItem.id || '');
        break;
      case FPADataTypes.FOLDER:
        issueCreateContext.folderId = String(selectedItem.id || '');
        break;
      default:
        break;
    }

    return issueCreateContext;
  }, [selectedItem]);

  const mapIssueRow = useCallback((raw: any, users: Array<{ id: string; name: string }>) => {
    const ident = String(raw?.ident || raw?.id || '');
    const createdBy = String(raw?.createdBy || '');
    const creatorName =
      users.find((u) => String(u.id) === createdBy)?.name ||
      String(raw?.createdByName || raw?.modifiedByName || '') ||
      '';
    const statusId = String(raw?.solutionStatus || raw?.status || '');
    const typeId = String(raw?.taskTemplateId || raw?.entryType || raw?.type || '');
    const status = issueStatusMap[statusId];
    return {
      id: ident,
      ident,
      title: String(raw?.title || raw?.name || raw?.subject || raw?.refId || 'Untitled issue'),
      entryTypeName: String(raw?.entryTypeName || issueTypeMap[typeId] || ''),
      entryTypeColor: issueTypeSettingsMap[typeId]?.color,
      creatorName,
      status: statusId,
      statusName: status?.title || statusId,
      statusColor: status?.color || statusColor(raw?.color) || '#B0BEC5',
      created: String(raw?.created || raw?.modified || ''),
      refId: String(raw?.refId || ''),
    };
  }, [issueStatusMap, issueTypeMap, issueTypeSettingsMap]);

  const loadIssues = useCallback(async () => {
    if (!selectedItem) {
      setRowsData([]);
      return;
    }
    setIsLoading(true);
    try {
      const response: any = await schedulerService.listIssue(
        new ListIssueRequest(store.Server, store.SessionId, {
          rangeStart: new Date(new Date().getFullYear() - 50, new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0),
          rangeEnd: new Date(new Date().getFullYear() + 50, new Date().getMonth(), new Date().getDate() + 1, 0, 0, 0, 0),
          anyTime: 'false',
          includePast: 'true',
          standardStatus: '0',
          userFilter: '1',
          limit: 100,
          offset: 0,
          orderBy1Desc: 'true',
          orderBy1Field: 'created',
          dateOfInterest: '0',
          groupUsers: '1',
          ...getContextFilter(),
        })
      );
      if (response?.result !== 'OK') {
        setRowsData([]);
        return;
      }
      const mapped = normalizeResponseItems(response.ITEMS || response.ITEM).map((entry: any) =>
        mapIssueRow(entry?.ITEM || entry, userData)
      );
      setRowsData(mapped);
    } finally {
      setIsLoading(false);
    }
  }, [getContextFilter, mapIssueRow, normalizeResponseItems, schedulerService, selectedItem, store.Server, store.SessionId, userData]);

  useEffect(() => {
    void Promise.all([getIssueStatusList(), getIssueTypes(), getUserData()]);
  }, [getIssueStatusList, getIssueTypes, getUserData]);

  useEffect(() => {
    if (selectedItem && issueStatusList.length > 0 && userData.length > 0) {
      void loadIssues();
    }
  }, [issueStatusList.length, loadIssues, selectedItem, userData.length]);

  const handleAddClick = useCallback(async () => {
    const templates = issueTemplates.length > 0 ? issueTemplates : await getIssueTemplates();
    if (templates.length === 0) {
      ToastQueue.negative(t('issue_messages.no_issue_templates_available', { ns: 'issue' }), { timeout: 2000 });
      return;
    }
    setShowTemplatePicker(true);
  }, [getIssueTemplates, issueTemplates, t]);

  const handleOpenIssueApp = useCallback((issueId: string) => {
    if (!issueId) return;
    invokeIssueApp(issueId);
  }, []);

  const columns = [
    { key: 'entryTypeName', name: t('issue_table_column.type', { ns: 'issue' }), width: 180 },
    { key: 'title', name: t('issue_table_column.title', { ns: 'issue' }), width: '1fr' },
    { key: 'creatorName', name: t('issue_table_column.creator', { ns: 'issue', defaultValue: 'Creator' }), width: 180 },
    { key: 'statusName', name: t('issue_table_column.state', { ns: 'issue' }), width: 180 },
    { key: 'created', name: t('issue_table_column.created', { ns: 'issue' }), width: 140 },
    { key: 'info', name: '\u00A0', width: 56 },
  ];

  return (
    <>
      <Flex direction="column" width="100%">
        <Flex direction="row" alignItems="center" justifyContent="start" UNSAFE_className={styles.header_row}>
          <Content UNSAFE_className={styles.heading_text}>
            {t('issue_detail.request', { ns: 'issue' })}
          </Content>
          <Flex direction="row" alignItems="center" justifyContent="center" gap="size-100" UNSAFE_className={styles.icon_add_parent}>
            <Content>
              <Link isQuiet onPress={handleAddClick}>
                <i className="bi bi-plus fs-5">
                  <View UNSAFE_className={styles.icon_add_text}>
                    {t('issue_template_picker.new_issue', { ns: 'issue' })}
                  </View>
                </i>
              </Link>
            </Content>
          </Flex>
        </Flex>

        {isLoading ? (
          <Flex width="100%" justifyContent="center" marginTop={10}>
            <ProgressCircle aria-label="Loading" isIndeterminate />
          </Flex>
        ) : rowsData.length > 0 ? (
          <View UNSAFE_className={styles.table_wrapper}>
            <TableView
              UNSAFE_className="tblLayoutComponent"
              aria-label="Context issues"
              selectionMode="single"
              selectionStyle="highlight"
              width={{ base: '100%', L: '100%', M: '100%' }}
              minHeight="80px"
              maxHeight={{ base: '1000px', L: '650px' }}
              onAction={() => {}}
            >
              <TableHeader columns={columns}>
                {(column: any) => (
                  <Column
                    showDivider
                    key={column.key}
                    width={column.width as any}
                    align={column.key === 'info' ? 'center' : 'start'}
                  >
                    {column.name}
                  </Column>
                )}
              </TableHeader>
              <TableBody items={rowsData}>
                {(item: any) => (
                  <Row>
                    {(columnKey: any) => {
                      if (columnKey === 'statusName') {
                        return (
                          <Cell>
                            <span className={styles.status_cell}>
                              <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={item.statusColor} />
                              {item.statusName}
                            </span>
                          </Cell>
                        );
                      }
                      if (columnKey === 'entryTypeName') {
                        return (
                          <Cell>
                            <span className={styles.status_cell}>
                              {item.entryTypeColor ? (
                                <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={item.entryTypeColor} />
                              ) : null}
                              {item.entryTypeName}
                            </span>
                          </Cell>
                        );
                      }
                      if (columnKey === 'created') {
                        return <Cell>{item.created ? new Date(item.created).toLocaleDateString('en-GB') : ''}</Cell>;
                      }
                      if (columnKey === 'info') {
                        return (
                          <Cell UNSAFE_className={styles.info_column_cell}>
                            <ActionButton isQuiet UNSAFE_className={styles.info_button} onPress={() => handleOpenIssueApp(String(item.id))}>
                              <InfoCircle />
                            </ActionButton>
                          </Cell>
                        );
                      }
                      return <Cell>{item[columnKey]}</Cell>;
                    }}
                  </Row>
                )}
              </TableBody>
            </TableView>
          </View>
        ) : (
          <View>{t('no_results', { ns: 'layout_components', defaultValue: 'No results' })}</View>
        )}
      </Flex>

      <DialogContainer onDismiss={() => setShowTemplatePicker(false)}>
        {showTemplatePicker ? (
          <IssueTemplatePickerDialog
            templates={issueTemplates}
            onSelect={(template) => {
              setSelectedTemplate(template);
              setShowTemplatePicker(false);
              setShowCreateDialog(true);
            }}
            onClose={() => setShowTemplatePicker(false)}
          />
        ) : null}
      </DialogContainer>

      <DialogContainer onDismiss={() => setShowCreateDialog(false)}>
        {showCreateDialog && selectedTemplate ? (
          <CreateIssueDialog
            template={selectedTemplate}
            issueTypes={issueTypes}
            statusList={issueStatusList}
            users={userData}
            context={getIssueCreateContext()}
            onClose={() => setShowCreateDialog(false)}
            onCreated={async (issue) => {
              await loadIssues();
            }}
          />
        ) : null}
      </DialogContainer>
    </>
  );
}

export const ContextIssueGridComponent = LayoutComponent(ContextIssueGrid);
